import React, { useCallback, useMemo } from "react";
import {
  FloatingPortal,
  size,
  useFloating,
  type Placement,
} from "@floating-ui/react";
import { Menu, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@components/Button";
import { ChevronIcon } from "@components/icons";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useUpdateCustomerCultureIdMutation } from "@services/customer/mutations";
import { languageMetadata } from "@/i18n";
import { queryClient } from "@/queryClient";

interface ILanguageSelectButtonProps {
  variant: "sidebar" | "topbar" | "auth";
  placement?: Placement;
}

export const LanguageSelectButton: React.FC<ILanguageSelectButtonProps> = (
  props,
) => {
  const { variant, placement } = props;
  const { t, i18n } = useTranslation();
  const { mutateAsync: updateCustomerCultureId } =
    useUpdateCustomerCultureIdMutation();

  const { toastApiError } = useApiErrorUtils();

  const languageOptions = useMemo(
    () =>
      Object.entries(languageMetadata.availableLanguages).map((language) => ({
        value: language[0],
        label: t(`language.${language[0]}`),
        abbreviation: t(`languageAbbreviation.${language[0]}`),
        icon: language[1].icon,
      })),
    [t],
  );

  const changeLanguage = useCallback(
    async (cultureId: string) =>
      variant === "auth"
        ? i18n.changeLanguage(cultureId)
        : i18n
            .changeLanguage(cultureId)
            .then(() => updateCustomerCultureId(cultureId))
            .then(() =>
              queryClient.invalidateQueries({
                queryKey: ["customer", "metadata"],
              }),
            )
            .catch(toastApiError),
    [variant, i18n, updateCustomerCultureId, toastApiError],
  );

  const currentLanguage = useMemo(
    () => languageOptions.find((language) => language.value === i18n.language),
    [languageOptions, i18n],
  );

  const { refs, floatingStyles } = useFloating({
    placement: placement ?? "bottom-start",
    strategy: "fixed",
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            minWidth: "224px",
          });
        },
      }),
    ],
  });

  return (
    <Menu>
      <div className="relative" ref={refs.setReference}>
        {currentLanguage && (
          <React.Fragment>
            {variant === "topbar" && (
              <Menu.Button
                as={Button}
                variant="secondary"
                className="aspect-square rounded-full border border-grey-hover bg-white p-0"
                aria-label={currentLanguage.label}
              >
                {currentLanguage && <currentLanguage.icon className="w-5" />}
              </Menu.Button>
            )}
            {variant === "sidebar" && (
              <Menu.Button
                className="flex w-full cursor-pointer gap-3 rounded-md px-3 py-2 hover:bg-grey-light"
                aria-label={currentLanguage.label}
              >
                <currentLanguage.icon className="w-6" />
                <div>
                  <span className="text-grey-dark">
                    {`${t("language.language")}: `}
                  </span>
                  <span>{currentLanguage.label}</span>
                </div>
              </Menu.Button>
            )}
            {variant === "auth" && (
              <Menu.Button
                className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-grey-light"
                aria-label={currentLanguage.label}
              >
                {currentLanguage && <currentLanguage.icon className="w-5" />}
                <span>{currentLanguage.abbreviation}</span>
                <ChevronIcon className="size-4" />
              </Menu.Button>
            )}
          </React.Fragment>
        )}
        <FloatingPortal>
          <div ref={refs.setFloating} style={floatingStyles} className="z-50">
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="mt-2 flex max-h-60 w-full flex-col gap-1 overflow-auto rounded-xl border border-grey-light bg-base-white p-2 text-base text-base-black shadow-lg focus:outline-none">
                {languageOptions.map((option) => (
                  <Menu.Item key={option.value}>
                    {({ active }) => (
                      <button
                        onClick={() => changeLanguage(option.value)}
                        className={twMerge(
                          "relative flex select-none flex-row items-center gap-2 rounded-full px-4 py-2 text-sm font-normal",
                          currentLanguage?.value === option.value &&
                            "bg-blue-light font-medium text-blue-dark",
                          active && "bg-base-light-grey",
                        )}
                      >
                        <option.icon className="w-5" />
                        {option.label}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </div>
        </FloatingPortal>
      </div>
    </Menu>
  );
};
